import { Building07, ShieldTick, User01 } from "@untitledui/icons";
import { type FC, type ReactNode, useState } from "react";
import {
	AvatarOnlineIndicator,
	VerifiedTick,
} from "@/components/base/avatar/base-components";
import { cx } from "@/utils/cx";

type AvatarSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export type AvatarStatus = "online" | "warning" | "error";

export type AvatarPlaceholder = "user" | "admin" | "organization";

export interface AvatarProps {
	size?: AvatarSize;
	className?: string;
	src?: string | null;
	alt?: string;
	/**
	 * Display a contrast border around the avatar.
	 */
	contrastBorder?: boolean;
	/**
	 * Display a badge (i.e. company logo).
	 */
	badge?: ReactNode;
	/**
	 * Display a status indicator (Figma: Online / Warning / Error).
	 */
	status?: AvatarStatus;
	/**
	 * Display a verified tick icon.
	 *
	 * @default false
	 */
	verified?: boolean;

	/**
	 * The initials of the user to display if no image is available.
	 */
	initials?: string;
	/**
	 * First-class placeholder matching Figma Placeholder enum.
	 * Organization uses orange background + Building07.
	 */
	placeholder?: AvatarPlaceholder;
	/**
	 * An icon to display if no image is available.
	 */
	placeholderIcon?: FC<{ className?: string }>;
	/**
	 * Custom className for the placeholder icon.
	 */
	iconClassName?: string;
	/**
	 * A custom node to display if no image / initials / placeholder is available.
	 */
	placeholderContent?: ReactNode;
	/**
	 * Custom background color class for the avatar circle.
	 */
	backgroundColor?: string;

	/**
	 * Whether the avatar should show a focus ring when the parent group is in focus.
	 * For example, when the avatar is wrapped inside a link.
	 *
	 * @default false
	 */
	focusable?: boolean;
}

const styles = {
	xxs: {
		root: "size-4 outline-[0.5px] -outline-offset-[0.5px]",
		initials: "text-xs font-semibold",
		icon: "size-3",
	},
	xs: {
		root: "size-6 outline-[0.5px] -outline-offset-[0.5px]",
		initials: "text-xs font-semibold",
		icon: "size-4",
	},
	sm: {
		root: "size-8 outline-[0.75px] -outline-offset-[0.75px]",
		initials: "text-sm font-semibold",
		icon: "size-5",
	},
	md: {
		root: "size-10 outline-1 -outline-offset-1",
		initials: "text-md font-semibold",
		icon: "size-6",
	},
	lg: {
		root: "size-12 outline-1 -outline-offset-1",
		initials: "text-lg font-semibold",
		icon: "size-7",
	},
	xl: {
		root: "size-14 outline-1 -outline-offset-1",
		initials: "text-xl font-semibold",
		icon: "size-8",
	},
	"2xl": {
		root: "size-16 outline-1 -outline-offset-1",
		initials: "text-display-xs font-semibold",
		icon: "size-8",
	},
};

const placeholderConfig = {
	user: { Icon: User01, background: "bg-avatar-bg" },
	admin: { Icon: ShieldTick, background: "bg-avatar-bg" },
	organization: { Icon: Building07, background: "bg-utility-orange-500" },
} as const;

/**
 * Circular user/entity indicator — photo, initials, or built-in placeholder.
 * Content resolves: src → initials → placeholder → placeholderIcon → placeholderContent → user.
 */
export const Avatar = ({
	contrastBorder = true,
	size = "md",
	src,
	alt,
	initials,
	placeholder,
	placeholderIcon: PlaceholderIcon,
	iconClassName,
	placeholderContent,
	badge,
	status,
	verified,
	focusable = false,
	backgroundColor,
	className,
}: AvatarProps) => {
	const [isFailed, setIsFailed] = useState(false);

	const showPhoto = Boolean(src && !isFailed);
	const resolvedPlaceholder = placeholder
		? placeholderConfig[placeholder]
		: null;

	const renderMainContent = () => {
		if (showPhoto) {
			return (
				<img
					data-avatar-img
					className="size-full rounded-full object-cover"
					src={src ?? undefined}
					alt={alt}
					onError={() => setIsFailed(true)}
				/>
			);
		}

		if (initials) {
			return (
				<span className={cx("text-fg-white", styles[size].initials)}>
					{initials}
				</span>
			);
		}

		if (resolvedPlaceholder) {
			const { Icon } = resolvedPlaceholder;
			return (
				<Icon
					className={cx("text-fg-white", styles[size].icon, iconClassName)}
				/>
			);
		}

		if (PlaceholderIcon) {
			return (
				<PlaceholderIcon
					className={cx("text-fg-white", styles[size].icon, iconClassName)}
				/>
			);
		}

		if (placeholderContent) {
			return placeholderContent;
		}

		return <User01 className={cx("text-fg-white", styles[size].icon)} />;
	};

	const renderBadgeContent = () => {
		if (status) {
			return (
				<AvatarOnlineIndicator
					status={status}
					size={size === "xxs" ? "xs" : size}
				/>
			);
		}

		if (verified) {
			return (
				<VerifiedTick
					size={size === "xxs" ? "xs" : size}
					className={cx(
						"absolute right-0 bottom-0",
						(size === "xxs" || size === "xs") && "-right-px -bottom-px",
					)}
				/>
			);
		}

		return badge;
	};

	const fallbackBackground =
		backgroundColor ??
		resolvedPlaceholder?.background ??
		(showPhoto ? undefined : "bg-avatar-bg");

	return (
		<div
			data-avatar
			className={cx(
				"relative inline-flex shrink-0 items-center justify-center rounded-full outline-transparent",
				fallbackBackground,
				focusable &&
					"group-outline-focus-ring group-focus-visible:outline-2 group-focus-visible:outline-offset-2",
				contrastBorder &&
					!backgroundColor &&
					"outline outline-avatar-contrast-border",
				styles[size].root,
				className,
			)}
		>
			{renderMainContent()}
			{renderBadgeContent()}
		</div>
	);
};
